import React from 'react';
import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import Head from 'next/head';
import { NormalizedCacheObject } from '@apollo/client';

import {
  Course,
  CourseDocument,
  CourseVideosDocument,
  CoursesVideosDocument,
  Video,
  VideoDocument,
} from '@edx/graphql/generated';
import { getStaticApolloClient } from '@edx/helpers/apollo';
import { parseISO8601DateTime } from '@edx/helpers/datetime';
import { route } from '@edx/helpers/url';
import { VideoDescription } from '@edx/components/video-description';
import { VideoPlayer } from '@edx/components/video-player';
import { VideoActions } from '@edx/components/video-actions';
import { VideoRelatedList } from '@edx/components/video-related-list';
import { Intro } from '@edx/components/typography';
import { VideoComments } from '@edx/components/video-comments';
import { PastDueModal } from '@edx/components/past-due-modal';

export const getStaticPaths = async (
  context: GetStaticPropsContext,
  courseSlug: string | undefined
): Promise<GetStaticPathsResult> => {
  const { apolloClient } = await getStaticApolloClient();
  let paths;

  if (courseSlug) {
    const { data } = await apolloClient.query({
      query: CourseDocument,
      variables: { slug: courseSlug },
    });

    paths = data.course.videos.map(({ slug }: Video) => ({ params: { courseSlug: courseSlug, video: slug } }));
  } else {
    const { data } = await apolloClient.query({ query: CoursesVideosDocument });
    paths = data.courses.reduce((acc: Array<{ params: { courseSlug: string; video: string } }>, course: Course) => {
      (course.videos as Video[]).forEach((video) => {
        acc.push({ params: { courseSlug: course.slug, video: video.slug } });
      });

      return acc;
    }, []);
  }

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = async (
  context: GetStaticPropsContext,
  courseSlug: string | undefined
): Promise<GetStaticPropsResult<Props>> => {
  const { apolloClient } = await getStaticApolloClient();
  const queryParams = context.params || {};
  courseSlug ??= queryParams.courseSlug as string;

  const videoQuery = await apolloClient.query({
    query: VideoDocument,
    variables: { courseSlug, slug: queryParams.video },
  });

  const courseVideosQuery = await apolloClient.query({
    query: CourseVideosDocument,
    variables: { slug: courseSlug },
  });

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      courseSlug,
      video: videoQuery.data.video,
      relatedVideos: courseVideosQuery.data.course.videos,
    },
  };
};

type Props = {
  initialApolloState: NormalizedCacheObject;
  courseSlug: string;
  video: Video;
  relatedVideos: Video[];
};

const VideoPage: React.FC<Props> = ({ courseSlug, video, relatedVideos }) => {
  const currentVideoIndex = relatedVideos.findIndex(({ id }) => id === video.id);
  const tags = video.tags.map(({ name }) => name).join(', ');

  return (
    <>
      <Head>
        {video.metaTitle && <title key="title">{video.metaTitle}</title>}

        {video.metaDescription && <meta key="description" name="description" content={video.metaDescription} />}

        <meta key="keywords" name="keywords" content={tags} />

        <meta
          key="article:published_time"
          name="article:published_time"
          content={parseISO8601DateTime(video.createdAt).toISOString()}
        />

        {/* Open Graph */}
        {video.metaTitle && <meta key="og:title" property="og:title" content={video.metaTitle} />}
        {video.metaDescription && (
          <meta key="og:description" property="og:description" content={video.metaDescription} />
        )}

        <link rel="canonical" href={route(video.resourcePath)} />

        {video.schemaOrg && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: video.schemaOrg }} />}
      </Head>

      <VideoPlayer
        video={video}
        nextVideo={relatedVideos?.[currentVideoIndex + 1]}
        prevVideo={relatedVideos?.[currentVideoIndex - 1]}
      />

      <div className="border-t border-gray-600 border-opacity-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between space-x-0 md:space-x-6 px-4 md:px-6 lg:px-10">
            <div className="w-full md:w-1/2">
              <VideoDescription video={video} />
            </div>

            <aside className="w-full md:w-2/5">
              <VideoActions courseSlug={video.course.slug} videoSlug={video.slug} videoId={video.id} />

              {relatedVideos.length > 0 && (
                <div className="h-96 overflow-y-scroll px-2 border-t border-r-0 md:border-r border-l-0 md:border-l border-gray-600 border-opacity-50">
                  <Intro tag="h3" size="small" className="block md:hidden text-gray-300 pt-8 pb-6">
                    A continuación
                  </Intro>

                  <VideoRelatedList courseSlug={courseSlug} relatedVideos={relatedVideos} />
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>

      <VideoComments courseSlug={courseSlug} videoSlug={video.slug} />

      <PastDueModal />
    </>
  );
};

export default VideoPage;
